import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const HRDashboard = () => {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  // Login IDs to exclude
  const LOGIN_IDS = [10253, 10406, 10130, 10354, 10321];

  // Filters
  const [selectedBranch, setSelectedBranch] = useState("");
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [branches, setBranches] = useState([]);

  // Statistics
  const [totalStaff, setTotalStaff] = useState({
    logins: 0,
    f11: 0,
    g13: 0,
  });
  const [totalPresent, setTotalPresent] = useState({
    f11: 0,
    g13: 0,
    byDate: {},
  });
  const [totalLate, setTotalLate] = useState({
    f11: 0,
    g13: 0,
    byDate: {},
  });
  const [totalAbsent, setTotalAbsent] = useState({
    f11: 0,
    g13: 0,
    byDate: {},
  });
  const [totalOnLeave, setTotalOnLeave] = useState({
    f11: 0,
    g13: 0,
    byDate: {},
  });
  const [birthdaysThisMonth, setBirthdaysThisMonth] = useState([]);
  const [anniversariesThisMonth, setAnniversariesThisMonth] = useState([]);
  const [lateEmployees, setLateEmployees] = useState([]);
  const [presentEmployees, setPresentEmployees] = useState([]);
  const [absentEmployees, setAbsentEmployees] = useState([]);
  const [onLeaveEmployees, setOnLeaveEmployees] = useState([]);

  // Loading state
  const [loading, setLoading] = useState(false);

  // Fetch Branches
  const fetchBranches = async () => {
    try {
      const response = await UserServices.branchName();
      if (response.status === 200) {
        const branchList = response.data.data || [];
        setBranches(branchList);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  // Fetch All Data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all staff
      const staffResponse = await UserServices.getactiveUsers(
        selectedBranch || branch_id,
        "1",
        "1000",
        "1",
        "",
        ""
      );

      if (staffResponse.status === 200) {
        const allStaff = staffResponse.data.data?.data || staffResponse.data.data || [];
        
        // Filter out login IDs
        const staffWithoutLogins = allStaff.filter(
          (staff) => !LOGIN_IDS.includes(parseInt(staff.id))
        );
        const loginStaff = allStaff.filter((staff) =>
          LOGIN_IDS.includes(parseInt(staff.id))
        );

        // Calculate total staff
        const f11Staff = staffWithoutLogins.filter(
          (staff) => staff.branch_id === 15 || staff.branch_id === "15"
        );
        const g13Staff = staffWithoutLogins.filter(
          (staff) => staff.branch_id === 1 || staff.branch_id === "1"
        );

        setTotalStaff({
          logins: loginStaff.length,
          f11: f11Staff.length,
          g13: g13Staff.length,
        });

        // Get staff IDs for F-11 and G-13
        const f11StaffIds = f11Staff.map((s) => parseInt(s.id));
        const g13StaffIds = g13Staff.map((s) => parseInt(s.id));

        // Fetch duty hours for all staff
        const dutyHoursResponse = await UserServices.getActiveEmployeHours(
          selectedBranch || branch_id,
          "1000",
          "1"
        );
        
        // Create a map of staff duty hours by staff ID and day
        const dutyHoursMap = {};
        if (dutyHoursResponse.status === 200) {
          const staffWithDutyHours = dutyHoursResponse.data.data?.data || dutyHoursResponse.data.data || [];
          staffWithDutyHours.forEach((staff) => {
            if (staff.duty_hours && staff.duty_hours.length > 0) {
              dutyHoursMap[staff.id] = {};
              staff.duty_hours.forEach((dh) => {
                if (dh.status === "1" || dh.status === 1) {
                  dutyHoursMap[staff.id][dh.day] = {
                    start_time: dh.start_time,
                    end_time: dh.end_time,
                  };
                }
              });
            }
          });
        }

        // Fetch attendance for date range
        const attendanceResponse = await UserServices.getEmployeeAttendence(
          selectedBranch || branch_id,
          { sdate: startDate, edate: endDate, start_time: null, end_time: null, gender: null },
          null,
          "1000",
          "1"
        );

        // Check if single date or date range
        const isDateRange = startDate !== endDate;
        const dateRange = [];
        if (isDateRange) {
          let currentDate = moment(startDate);
          const end = moment(endDate);
          while (currentDate.isSameOrBefore(end)) {
            dateRange.push(currentDate.format("YYYY-MM-DD"));
            currentDate.add(1, "day");
          }
        } else {
          dateRange.push(startDate);
        }

        if (attendanceResponse.status === 200) {
          const attendance = attendanceResponse.data.data?.data || attendanceResponse.data.data || [];
          
          console.log("Attendance records fetched:", attendance.length);
          console.log("Sample attendance record:", attendance[0]);
          
          // Group attendance by date
          const attendanceByDateMap = {};
          dateRange.forEach((date) => {
            attendanceByDateMap[date] = {
              date,
              f11Present: 0,
              f11Late: 0,
              f11Absent: 0,
              g13Present: 0,
              g13Late: 0,
              g13Absent: 0,
            };
          });

          // Track late employees with details
          const lateEmployeesList = [];
          const presentEmployeesList = [];
          const absentEmployeesList = [];

          // Process attendance records - API returns both Present and Absent
          attendance.forEach((att) => {
            const staffId = parseInt(att.attendee_id);
            const attDate = att.date; // Use the date field from API
            
            if (!attDate || !attendanceByDateMap[attDate]) return;
            
            // Skip login IDs
            if (LOGIN_IDS.includes(staffId)) return;

            const isF11 = f11StaffIds.includes(staffId);
            const isG13 = g13StaffIds.includes(staffId);
            const isPresent = att.attendance_status === "Present";
            const isAbsent = att.attendance_status === "Absent";

            // Calculate late: compare checkin_time_24h with duty hours start_time
            let isLate = false;
            let lateMinutes = 0;
            let lateHours = 0;
            let lateTimeDisplay = "";
            let dutyStartTime = "";
            let dutyEndTime = "";
            let dutyStartTime12h = "";
            let dutyEndTime12h = "";
            
            if (isPresent && att.checkin_time_24h) {
              const dayName = moment(attDate).format("dddd"); // Get day name (Monday, Tuesday, etc.)
              const staffDutyHours = dutyHoursMap[staffId];
              if (staffDutyHours && staffDutyHours[dayName] && staffDutyHours[dayName].start_time) {
                try {
                  const checkinTime = moment(att.checkin_time_24h, "HH:mm:ss");
                  const dutyStart = moment(staffDutyHours[dayName].start_time, "HH:mm");
                  if (checkinTime.isValid() && dutyStart.isValid() && checkinTime.isAfter(dutyStart)) {
                    isLate = true;
                    lateMinutes = checkinTime.diff(dutyStart, "minutes");
                    lateHours = Math.floor(lateMinutes / 60);
                    const remainingMinutes = lateMinutes % 60;
                    
                    if (lateHours > 0) {
                      lateTimeDisplay = `${lateHours} hour${lateHours > 1 ? "s" : ""} ${remainingMinutes > 0 ? remainingMinutes + " min" : ""}`.trim();
                    } else {
                      lateTimeDisplay = `${lateMinutes} min`;
                    }
                    
                    // Store duty hours in 24-hour format for calculation
                    dutyStartTime = staffDutyHours[dayName].start_time;
                    dutyEndTime = staffDutyHours[dayName].end_time || "";
                    
                    // Convert duty hours to 12-hour format
                    if (dutyStartTime) {
                      const startTime12h = moment(dutyStartTime, "HH:mm");
                      if (startTime12h.isValid()) {
                        dutyStartTime12h = startTime12h.format("hh:mm A");
                      } else {
                        dutyStartTime12h = dutyStartTime;
                      }
                    }
                    
                    if (dutyEndTime) {
                      const endTime12h = moment(dutyEndTime, "HH:mm");
                      if (endTime12h.isValid()) {
                        dutyEndTime12h = endTime12h.format("hh:mm A");
                      } else {
                        dutyEndTime12h = dutyEndTime;
                      }
                    }
                  }
                } catch (e) {
                  console.error("Error comparing times:", e);
                }
              }
            }

            // Get employee details
            const attendee = att.attendee || {};
            const empName = attendee.first_name && attendee.last_name 
              ? `${attendee.first_name} ${attendee.last_name}`
              : att.user_name || "N/A";
            
            // Find staff details for branch, designation, department
            const staffDetails = [...f11Staff, ...g13Staff].find(s => parseInt(s.id) === staffId);
            
            // Format check-in time to 12-hour format with AM/PM
            let checkinTime12h = "N/A";
            let checkoutTime12h = "N/A";
            if (att.checkin_time_24h) {
              try {
                const time24h = moment(att.checkin_time_24h, "HH:mm:ss");
                if (time24h.isValid()) {
                  checkinTime12h = time24h.format("hh:mm A");
                } else if (att.checkin_time_12h) {
                  checkinTime12h = att.checkin_time_12h;
                }
              } catch (e) {
                checkinTime12h = att.checkin_time_12h || "N/A";
              }
            } else if (att.checkin_time_12h) {
              checkinTime12h = att.checkin_time_12h;
            }
            
            if (att.checkout_time_24h) {
              try {
                const time24h = moment(att.checkout_time_24h, "HH:mm:ss");
                if (time24h.isValid()) {
                  checkoutTime12h = time24h.format("hh:mm A");
                } else if (att.checkout_time_12h) {
                  checkoutTime12h = att.checkout_time_12h;
                }
              } catch (e) {
                checkoutTime12h = att.checkout_time_12h || "N/A";
              }
            } else if (att.checkout_time_12h) {
              checkoutTime12h = att.checkout_time_12h;
            }
            
            const employeeData = {
              id: staffId,
              name: empName,
              date: attDate,
              checkin_time: checkinTime12h,
              checkout_time: checkoutTime12h,
              working_hours: att.working_hours || "N/A",
              branch: staffDetails?.branch_name || (isF11 ? "F-11" : isG13 ? "G-13" : "N/A"),
              branch_id: att.branch_id,
              designation: att.designation || staffDetails?.designation_name || staffDetails?.designation || "N/A",
              department: staffDetails?.department_name || staffDetails?.department || "N/A",
            };
            
            // Add to late employees list if late
            if (isLate && isPresent) {
              lateEmployeesList.push({
                ...employeeData,
                duty_start_time: dutyStartTime12h || dutyStartTime,
                duty_end_time: dutyEndTime12h || dutyEndTime,
                late_minutes: lateMinutes,
                late_time: lateTimeDisplay,
              });
            }
            
            // Add to present employees list (not late)
            if (isPresent && !isLate) {
              // Get duty hours for present employees (even if not late)
              let presentDutyStartTime12h = "";
              let presentDutyEndTime12h = "";
              let presentDutyStartTime = "";
              let presentDutyEndTime = "";
              
              if (att.checkin_time_24h) {
                const dayName = moment(attDate).format("dddd");
                const staffDutyHours = dutyHoursMap[staffId];
                if (staffDutyHours && staffDutyHours[dayName] && staffDutyHours[dayName].start_time) {
                  presentDutyStartTime = staffDutyHours[dayName].start_time;
                  presentDutyEndTime = staffDutyHours[dayName].end_time || "";
                  
                  // Convert to 12-hour format
                  if (presentDutyStartTime) {
                    const startTime12h = moment(presentDutyStartTime, "HH:mm");
                    if (startTime12h.isValid()) {
                      presentDutyStartTime12h = startTime12h.format("hh:mm A");
                    } else {
                      presentDutyStartTime12h = presentDutyStartTime;
                    }
                  }
                  
                  if (presentDutyEndTime) {
                    const endTime12h = moment(presentDutyEndTime, "HH:mm");
                    if (endTime12h.isValid()) {
                      presentDutyEndTime12h = endTime12h.format("hh:mm A");
                    } else {
                      presentDutyEndTime12h = presentDutyEndTime;
                    }
                  }
                }
              }
              
              presentEmployeesList.push({
                ...employeeData,
                duty_start_time: presentDutyStartTime12h || presentDutyStartTime || "N/A",
                duty_end_time: presentDutyEndTime12h || presentDutyEndTime || "N/A",
              });
            }
            
            // Add to absent employees list
            if (isAbsent) {
              absentEmployeesList.push({
                ...employeeData,
                reason: att.reason || "N/A",
                remarks: att.remarks || "N/A",
              });
            }

            if (isF11) {
              if (isPresent) {
                attendanceByDateMap[attDate].f11Present++;
                if (isLate) {
                  attendanceByDateMap[attDate].f11Late++;
                }
              } else if (isAbsent) {
                attendanceByDateMap[attDate].f11Absent++;
              }
            }
            if (isG13) {
              if (isPresent) {
                attendanceByDateMap[attDate].g13Present++;
                if (isLate) {
                  attendanceByDateMap[attDate].g13Late++;
                }
              } else if (isAbsent) {
                attendanceByDateMap[attDate].g13Absent++;
              }
            }
          });

          // Sort late employees by date and then by late minutes (most late first)
          lateEmployeesList.sort((a, b) => {
            const dateCompare = moment(a.date).diff(moment(b.date));
            if (dateCompare !== 0) return dateCompare;
            return b.late_minutes - a.late_minutes;
          });

          // Sort present employees by date and then by name
          presentEmployeesList.sort((a, b) => {
            const dateCompare = moment(a.date).diff(moment(b.date));
            if (dateCompare !== 0) return dateCompare;
            return a.name.localeCompare(b.name);
          });

          // Sort absent employees by date and then by name
          absentEmployeesList.sort((a, b) => {
            const dateCompare = moment(a.date).diff(moment(b.date));
            if (dateCompare !== 0) return dateCompare;
            return a.name.localeCompare(b.name);
          });

          setLateEmployees(lateEmployeesList);
          setPresentEmployees(presentEmployeesList);
          setAbsentEmployees(absentEmployeesList);

          // Calculate totals (sum across all dates or single date)
          const totalF11Present = Object.values(attendanceByDateMap).reduce((sum, d) => sum + d.f11Present, 0);
          const totalG13Present = Object.values(attendanceByDateMap).reduce((sum, d) => sum + d.g13Present, 0);
          const totalF11Late = Object.values(attendanceByDateMap).reduce((sum, d) => sum + d.f11Late, 0);
          const totalG13Late = Object.values(attendanceByDateMap).reduce((sum, d) => sum + d.g13Late, 0);
          const totalF11Absent = Object.values(attendanceByDateMap).reduce((sum, d) => sum + d.f11Absent, 0);
          const totalG13Absent = Object.values(attendanceByDateMap).reduce((sum, d) => sum + d.g13Absent, 0);

          // Build date breakdown objects
          const presentByDate = {};
          const lateByDate = {};
          const absentByDate = {};
          
          Object.values(attendanceByDateMap).forEach((dateData) => {
            const dateKey = dateData.date;
            presentByDate[dateKey] = {
              f11: dateData.f11Present,
              g13: dateData.g13Present,
            };
            lateByDate[dateKey] = {
              f11: dateData.f11Late,
              g13: dateData.g13Late,
            };
            absentByDate[dateKey] = {
              f11: dateData.f11Absent,
              g13: dateData.g13Absent,
            };
          });

          setTotalPresent({
            f11: totalF11Present,
            g13: totalG13Present,
            byDate: presentByDate,
          });

          setTotalLate({
            f11: totalF11Late,
            g13: totalG13Late,
            byDate: lateByDate,
          });

          setTotalAbsent({
            f11: totalF11Absent,
            g13: totalG13Absent,
            byDate: absentByDate,
          });
        } else {
          // If no attendance data, set absent to 0 (only count explicit Absent status)
          setTotalAbsent({
            f11: 0,
            g13: 0,
            byDate: {},
          });
          setAbsentEmployees([]);
        }

        // Fetch leave applications for date range
        const leaveResponse = await UserServices.getLeaveApplications(
          selectedBranch || branch_id,
          "1000",
          "1"
        );

        const onLeaveEmployeesList = [];
        
        if (leaveResponse.status === 200) {
          const leaves = leaveResponse.data.data || [];
          
          // Filter leaves that overlap with date range
          const onLeaveInRange = leaves.filter(
            (leave) =>
              leave.application_status === "Approved" &&
              moment(leave.leave_from_date).isSameOrBefore(endDate) &&
              moment(leave.leave_to_date).isSameOrAfter(startDate)
          );

          const f11OnLeave = onLeaveInRange.filter((leave) => {
            const userId = leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id;
            const userIdInt = parseInt(userId);
            // Exclude login IDs
            if (LOGIN_IDS.includes(userIdInt)) return false;
            return f11StaffIds.includes(userIdInt);
          });
          const g13OnLeave = onLeaveInRange.filter((leave) => {
            const userId = leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id;
            const userIdInt = parseInt(userId);
            // Exclude login IDs
            if (LOGIN_IDS.includes(userIdInt)) return false;
            return g13StaffIds.includes(userIdInt);
          });

          // Build on leave by date and employee list
          const onLeaveByDate = {};
          dateRange.forEach((date) => {
            const f11OnLeaveForDate = onLeaveInRange.filter((leave) => {
              const userId = leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id;
              const userIdInt = parseInt(userId);
              // Exclude login IDs
              if (LOGIN_IDS.includes(userIdInt)) return false;
              const isF11 = f11StaffIds.includes(userIdInt);
              return isF11 && moment(date).isBetween(
                leave.leave_from_date,
                leave.leave_to_date,
                null,
                "[]"
              );
            });
            
            const g13OnLeaveForDate = onLeaveInRange.filter((leave) => {
              const userId = leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id;
              const userIdInt = parseInt(userId);
              // Exclude login IDs
              if (LOGIN_IDS.includes(userIdInt)) return false;
              const isG13 = g13StaffIds.includes(userIdInt);
              return isG13 && moment(date).isBetween(
                leave.leave_from_date,
                leave.leave_to_date,
                null,
                "[]"
              );
            });
            
            onLeaveByDate[date] = {
              f11: f11OnLeaveForDate.length,
              g13: g13OnLeaveForDate.length,
            };
            
            // Add employees to on leave list
            [...f11OnLeaveForDate, ...g13OnLeaveForDate].forEach((leave) => {
              const userId = leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id;
              const userIdInt = parseInt(userId);
              // Skip login IDs (already filtered, but double check)
              if (LOGIN_IDS.includes(userIdInt)) return;
              
              const staffDetails = [...f11Staff, ...g13Staff].find(s => parseInt(s.id) === userIdInt);
              const isF11 = f11StaffIds.includes(userIdInt);
              
              onLeaveEmployeesList.push({
                id: userIdInt,
                name: staffDetails 
                  ? `${staffDetails.first_name || ""} ${staffDetails.last_name || ""}`.trim() || "N/A"
                  : leave.user_info?.first_name && leave.user_info?.last_name
                  ? `${leave.user_info.first_name} ${leave.user_info.last_name}`
                  : "N/A",
                date: date,
                leave_from_date: leave.leave_from_date,
                leave_to_date: leave.leave_to_date,
                leave_type: leave.leave_type || "N/A",
                reason: leave.reason || leave.remarks || "N/A",
                branch: staffDetails?.branch_name || (isF11 ? "F-11" : "G-13"),
                branch_id: isF11 ? 15 : 1,
                designation: staffDetails?.designation_name || staffDetails?.designation || "N/A",
                department: staffDetails?.department_name || staffDetails?.department || "N/A",
              });
            });
          });
          
          // Sort on leave employees by date and then by name
          onLeaveEmployeesList.sort((a, b) => {
            const dateCompare = moment(a.date).diff(moment(b.date));
            if (dateCompare !== 0) return dateCompare;
            return a.name.localeCompare(b.name);
          });

          setTotalOnLeave({
            f11: f11OnLeave.length,
            g13: g13OnLeave.length,
            byDate: onLeaveByDate,
          });
          
          setOnLeaveEmployees(onLeaveEmployeesList);
        } else {
          setTotalOnLeave({
            f11: 0,
            g13: 0,
            byDate: {},
          });
          setOnLeaveEmployees([]);
        }

        // Fetch birthdays this month (exclude logins)
        const currentMonth = moment().month();
        const currentYear = moment().year();
        const birthdays = staffWithoutLogins
          .filter((member) => {
            if (!member.dob) return false;
            try {
              const dob = moment(member.dob);
              if (!dob.isValid()) return false;
              const dobMonth = dob.month();
              return dobMonth === currentMonth;
            } catch (e) {
              console.error("Error parsing DOB:", member.dob, e);
              return false;
            }
          })
          .map((member) => {
            try {
              const dob = moment(member.dob);
              return {
                ...member,
                dob: member.dob, // Keep original for sorting
                birthday_date: dob.format("MMM DD"),
                age: moment().diff(dob, "years"),
              };
            } catch (e) {
              return null;
            }
          })
          .filter(Boolean)
          .sort((a, b) => {
            try {
              return moment(a.dob).date() - moment(b.dob).date();
            } catch (e) {
              return 0;
            }
          });

        setBirthdaysThisMonth(birthdays);

        // Fetch anniversaries this month (exclude logins)
        const today = moment();
        const anniversaries = staffWithoutLogins
          .filter((member) => {
            if (!member.joining) return false;
            try {
              const joiningDate = moment(member.joining);
              if (!joiningDate.isValid()) return false;
              const anniversaryThisYear = joiningDate.year(today.year());
              const anniversaryMonth = anniversaryThisYear.month();
              return (
                anniversaryMonth === currentMonth &&
                anniversaryThisYear.isSameOrAfter(today)
              );
            } catch (e) {
              console.error("Error parsing joining date:", member.joining, e);
              return false;
            }
          })
          .map((member) => {
            try {
              const joiningDate = moment(member.joining);
              const anniversaryThisYear = joiningDate.year(today.year());
              const yearsOfService = today.diff(joiningDate, "years");
              return {
                ...member,
                anniversary_date: anniversaryThisYear.format("MMM DD, YYYY"),
                years_of_service: yearsOfService,
              };
            } catch (e) {
              return null;
            }
          })
          .filter(Boolean)
          .sort((a, b) =>
            moment(a.anniversary_date).diff(moment(b.anniversary_date))
          );

        setAnniversariesThisMonth(anniversaries);
        
        console.log("Birthdays found:", birthdays.length);
        console.log("Anniversaries found:", anniversaries.length);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedBranch, startDate, endDate, branch_id]);

  const handleGo = () => {
    fetchData();
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="row mb-3">
            <div className="col-12">
              <h4 className="mb-0">HR Dashboard</h4>
              <p className="text-muted">Human Resource Management Overview</p>
            </div>
          </div>

          {/* Filters */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="card radius-10 custom-shadow">
                <div className="card-body">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label">Branch</label>
                      <select
                        className="form-select"
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.target.value)}
                      >
                        <option value="">All Branches</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <button
                        className="btn btn-primary w-100"
                        onClick={handleGo}
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Go"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Tiles - Current Date Only */}
          <div className="row mb-4">
            {/* Get current date data (use endDate or startDate if single date) */}
            {(() => {
              const currentDateKey = startDate === endDate ? startDate : endDate;
              const currentPresent = totalPresent.byDate?.[currentDateKey] || { f11: 0, g13: 0 };
              const currentLate = totalLate.byDate?.[currentDateKey] || { f11: 0, g13: 0 };
              const currentAbsent = totalAbsent.byDate?.[currentDateKey] || { f11: 0, g13: 0 };
              const currentOnLeave = totalOnLeave.byDate?.[currentDateKey] || { f11: 0, g13: 0 };
              const currentLateEmployees = startDate === endDate 
                ? lateEmployees.filter(emp => emp.date === startDate)
                : lateEmployees.filter(emp => emp.date === endDate);
              
              return (
                <>
                  {/* Total Staff Tile */}
                  <div className="col-12 col-md-6 col-lg-4 mb-3">
                    <div className="card radius-10 custom-shadow h-100 border-primary">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-2">Total Staff</h6>
                        {loading ? (
                          <Skeleton height={30} />
                        ) : (
                          <>
                            <h2 className="mb-3 fw-bold">{totalStaff.f11 + totalStaff.g13 + totalStaff.logins}</h2>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              {/* <span className="badge bg-info fs-6 px-3 py-2">Logins: {totalStaff.logins}</span> */}
                              <span className="badge bg-primary fs-6 px-3 py-2">F-11: {totalStaff.f11}</span>
                              <span className="badge bg-success fs-6 px-3 py-2">G-13: {totalStaff.g13}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Total Present Tile */}
                  <div className="col-12 col-md-6 col-lg-4 mb-3">
                    <div className="card radius-10 custom-shadow h-100 border-success">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-2">Total Present ({moment(currentDateKey).format("MMM DD")})</h6>
                        {loading ? (
                          <Skeleton height={30} />
                        ) : (
                          <>
                            <h2 className="mb-3 fw-bold text-success">{currentPresent.f11 + currentPresent.g13}</h2>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              <span className="badge bg-success fs-6 px-3 py-2">F-11: {currentPresent.f11}</span>
                              <span className="badge bg-success fs-6 px-3 py-2">G-13: {currentPresent.g13}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Total Late Tile */}
                  <div className="col-12 col-md-6 col-lg-4 mb-3">
                    <div className="card radius-10 custom-shadow h-100 border-warning">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-2">Total Late ({moment(currentDateKey).format("MMM DD")})</h6>
                        {loading ? (
                          <Skeleton height={30} />
                        ) : (
                          <>
                            <h2 className="mb-3 fw-bold text-warning">{currentLate.f11 + currentLate.g13}</h2>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              <span className="badge bg-warning fs-6 px-3 py-2">F-11: {currentLate.f11}</span>
                              <span className="badge bg-warning fs-6 px-3 py-2">G-13: {currentLate.g13}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Total Absent Tile */}
                  <div className="col-12 col-md-6 col-lg-4 mb-3">
                    <div className="card radius-10 custom-shadow h-100 border-danger">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-2">Total Absent ({moment(currentDateKey).format("MMM DD")})</h6>
                        {loading ? (
                          <Skeleton height={30} />
                        ) : (
                          <>
                            <h2 className="mb-3 fw-bold text-danger">{currentAbsent.f11 + currentAbsent.g13}</h2>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              <span className="badge bg-danger fs-6 px-3 py-2">F-11: {currentAbsent.f11}</span>
                              <span className="badge bg-danger fs-6 px-3 py-2">G-13: {currentAbsent.g13}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Total On Leave Tile */}
                  <div className="col-12 col-md-6 col-lg-4 mb-3">
                    <div className="card radius-10 custom-shadow h-100 border-info">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-2">Total On Leave ({moment(currentDateKey).format("MMM DD")})</h6>
                        {loading ? (
                          <Skeleton height={30} />
                        ) : (
                          <>
                            <h2 className="mb-3 fw-bold text-info">{currentOnLeave.f11 + currentOnLeave.g13}</h2>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              <span className="badge bg-info fs-6 px-3 py-2">F-11: {currentOnLeave.f11}</span>
                              <span className="badge bg-info fs-6 px-3 py-2">G-13: {currentOnLeave.g13}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Late Employees Count Tile */}
                  <div className="col-12 col-md-6 col-lg-4 mb-3">
                    <div className="card radius-10 custom-shadow h-100 border-warning">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-2">Late Employees ({moment(currentDateKey).format("MMM DD")})</h6>
                        {loading ? (
                          <Skeleton height={30} />
                        ) : (
                          <>
                            <h2 className="mb-3 fw-bold text-warning">{currentLateEmployees.length}</h2>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              <span className="badge bg-warning fs-6 px-3 py-2">
                                F-11: {currentLateEmployees.filter(emp => emp.branch_id === 15 || emp.branch === "F-11").length}
                              </span>
                              <span className="badge bg-warning fs-6 px-3 py-2">
                                G-13: {currentLateEmployees.filter(emp => emp.branch_id === 1 || emp.branch === "G-13").length}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Detailed Branch-wise Statistics Grid */}
          <div className="row mb-4">
            {/* Total Present - Detailed */}
            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow border-success">
                <div className="card-header bg-transparent border-bottom">
                  <h5 className="mb-0 text-success">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Total Present - Branch Wise
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={2} height={60} />
                  ) : (
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <h6 className="text-muted mb-2">F-11 Branch</h6>
                          <h1 className="mb-0 text-success fw-bold">{totalPresent.f11}</h1>
                          {Object.keys(totalPresent.byDate || {}).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(totalPresent.byDate || {})
                                .sort((a, b) => moment(a[0]).diff(moment(b[0])))
                                .map(([date, counts]) => (
                                  <div key={date} className="small">
                                    <span className="text-muted">{moment(date).format("MMM DD")}:</span>{" "}
                                    <span className="badge bg-success">{counts.f11}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <h6 className="text-muted mb-2">G-13 Branch</h6>
                          <h1 className="mb-0 text-success fw-bold">{totalPresent.g13}</h1>
                          {Object.keys(totalPresent.byDate || {}).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(totalPresent.byDate || {})
                                .sort((a, b) => moment(a[0]).diff(moment(b[0])))
                                .map(([date, counts]) => (
                                  <div key={date} className="small">
                                    <span className="text-muted">{moment(date).format("MMM DD")}:</span>{" "}
                                    <span className="badge bg-success">{counts.g13}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Total Late - Detailed */}
            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow border-warning">
                <div className="card-header bg-transparent border-bottom">
                  <h5 className="mb-0 text-warning">
                    <i className="bi bi-clock-history me-2"></i>
                    Total Late - Branch Wise
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={2} height={60} />
                  ) : (
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <h6 className="text-muted mb-2">F-11 Branch</h6>
                          <h1 className="mb-0 text-warning fw-bold">{totalLate.f11}</h1>
                          {Object.keys(totalLate.byDate || {}).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(totalLate.byDate || {})
                                .sort((a, b) => moment(a[0]).diff(moment(b[0])))
                                .map(([date, counts]) => (
                                  <div key={date} className="small">
                                    <span className="text-muted">{moment(date).format("MMM DD")}:</span>{" "}
                                    <span className="badge bg-warning">{counts.f11}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <h6 className="text-muted mb-2">G-13 Branch</h6>
                          <h1 className="mb-0 text-warning fw-bold">{totalLate.g13}</h1>
                          {Object.keys(totalLate.byDate || {}).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(totalLate.byDate || {})
                                .sort((a, b) => moment(a[0]).diff(moment(b[0])))
                                .map(([date, counts]) => (
                                  <div key={date} className="small">
                                    <span className="text-muted">{moment(date).format("MMM DD")}:</span>{" "}
                                    <span className="badge bg-warning">{counts.g13}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Total Absent - Detailed */}
            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow border-danger">
                <div className="card-header bg-transparent border-bottom">
                  <h5 className="mb-0 text-danger">
                    <i className="bi bi-x-circle-fill me-2"></i>
                    Total Absent - Branch Wise
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={2} height={60} />
                  ) : (
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <h6 className="text-muted mb-2">F-11 Branch</h6>
                          <h1 className="mb-0 text-danger fw-bold">{totalAbsent.f11}</h1>
                          {Object.keys(totalAbsent.byDate || {}).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(totalAbsent.byDate || {})
                                .sort((a, b) => moment(a[0]).diff(moment(b[0])))
                                .map(([date, counts]) => (
                                  <div key={date} className="small">
                                    <span className="text-muted">{moment(date).format("MMM DD")}:</span>{" "}
                                    <span className="badge bg-danger">{counts.f11}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <h6 className="text-muted mb-2">G-13 Branch</h6>
                          <h1 className="mb-0 text-danger fw-bold">{totalAbsent.g13}</h1>
                          {Object.keys(totalAbsent.byDate || {}).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(totalAbsent.byDate || {})
                                .sort((a, b) => moment(a[0]).diff(moment(b[0])))
                                .map(([date, counts]) => (
                                  <div key={date} className="small">
                                    <span className="text-muted">{moment(date).format("MMM DD")}:</span>{" "}
                                    <span className="badge bg-danger">{counts.g13}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Total On Leave - Detailed */}
            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow border-info">
                <div className="card-header bg-transparent border-bottom">
                  <h5 className="mb-0 text-info">
                    <i className="bi bi-calendar-x me-2"></i>
                    Total On Leave - Branch Wise
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={2} height={60} />
                  ) : (
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <h6 className="text-muted mb-2">F-11 Branch</h6>
                          <h1 className="mb-0 text-info fw-bold">{totalOnLeave.f11}</h1>
                          {Object.keys(totalOnLeave.byDate || {}).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(totalOnLeave.byDate || {})
                                .sort((a, b) => moment(a[0]).diff(moment(b[0])))
                                .map(([date, counts]) => (
                                  <div key={date} className="small">
                                    <span className="text-muted">{moment(date).format("MMM DD")}:</span>{" "}
                                    <span className="badge bg-info">{counts.f11}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <h6 className="text-muted mb-2">G-13 Branch</h6>
                          <h1 className="mb-0 text-info fw-bold">{totalOnLeave.g13}</h1>
                          {Object.keys(totalOnLeave.byDate || {}).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(totalOnLeave.byDate || {})
                                .sort((a, b) => moment(a[0]).diff(moment(b[0])))
                                .map(([date, counts]) => (
                                  <div key={date} className="small">
                                    <span className="text-muted">{moment(date).format("MMM DD")}:</span>{" "}
                                    <span className="badge bg-info">{counts.g13}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Overview Tab Content */}
          <div className="row">


            {/* Present Employees List */}
            <div className="col-12 mb-3">
              <div className="card radius-10 custom-shadow border-success">
                <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-success">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Present Employees List
                  </h5>
                  {!loading && (
                    <span className="badge bg-success fs-6">
                      Total: {presentEmployees.length}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : presentEmployees.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-striped">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Name</th>
                            <th>Date</th>
                            <th>Duty Hours</th>
                            <th>Check-in Time</th>
                            <th>Check-out Time</th>
                            <th>Working Hours</th>
                            <th>Branch</th>
                            <th>Designation</th>
                            <th>Department</th>
                          </tr>
                        </thead>
                        <tbody>
                          {presentEmployees.map((emp, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                <strong>{emp.name}</strong>
                              </td>
                              <td>
                                <span className="badge bg-info">
                                  {moment(emp.date).format("MMM DD, YYYY")}
                                </span>
                              </td>
                              <td>
                                {emp.duty_start_time && emp.duty_start_time !== "N/A" ? (
                                  <span className="badge bg-secondary">
                                    {emp.duty_start_time} - {emp.duty_end_time || "N/A"}
                                  </span>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>
                                <span className="badge bg-success">{emp.checkin_time}</span>
                              </td>
                              <td>
                                <span className="badge bg-primary">{emp.checkout_time}</span>
                              </td>
                              <td>
                                <span className="badge bg-info">{emp.working_hours}</span>
                              </td>
                              <td>{emp.branch}</td>
                              <td>{emp.designation}</td>
                              <td>{emp.department}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3">No present employees found in the selected date range</p>
                  )}
                </div>
              </div>
            </div>

            {/* Absent Employees List */}
            <div className="col-12 mb-3">
              <div className="card radius-10 custom-shadow border-danger">
                <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-danger">
                    <i className="bi bi-x-circle-fill me-2"></i>
                    Absent Employees List
                  </h5>
                  {!loading && (
                    <span className="badge bg-danger fs-6">
                      Total: {absentEmployees.length}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : absentEmployees.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-striped">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Name</th>
                            <th>Date</th>
                            <th>Reason</th>
                            <th>Remarks</th>
                            <th>Branch</th>
                            <th>Designation</th>
                            <th>Department</th>
                          </tr>
                        </thead>
                        <tbody>
                          {absentEmployees.map((emp, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                <strong>{emp.name}</strong>
                              </td>
                              <td>
                                <span className="badge bg-danger">
                                  {moment(emp.date).format("MMM DD, YYYY")}
                                </span>
                              </td>
                              <td>{emp.reason}</td>
                              <td>{emp.remarks}</td>
                              <td>{emp.branch}</td>
                              <td>{emp.designation}</td>
                              <td>{emp.department}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3">No absent employees found in the selected date range</p>
                  )}
                </div>
              </div>
            </div>

            {/* On Leave Employees List */}
            <div className="col-12 mb-3">
              <div className="card radius-10 custom-shadow border-info">
                <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-info">
                    <i className="bi bi-calendar-x me-2"></i>
                    On Leave Employees List
                  </h5>
                  {!loading && (
                    <span className="badge bg-info fs-6">
                      Total: {onLeaveEmployees.length}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : onLeaveEmployees.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-striped">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Name</th>
                            <th>Date</th>
                            <th>Leave From</th>
                            <th>Leave To</th>
                            <th>Leave Type</th>
                            <th>Reason</th>
                            <th>Branch</th>
                            <th>Designation</th>
                            <th>Department</th>
                          </tr>
                        </thead>
                        <tbody>
                          {onLeaveEmployees.map((emp, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                <strong>{emp.name}</strong>
                              </td>
                              <td>
                                <span className="badge bg-info">
                                  {moment(emp.date).format("MMM DD, YYYY")}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {moment(emp.leave_from_date).format("MMM DD, YYYY")}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {moment(emp.leave_to_date).format("MMM DD, YYYY")}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-secondary">{emp.leave_type}</span>
                              </td>
                              <td>{emp.reason}</td>
                              <td>{emp.branch}</td>
                              <td>{emp.designation}</td>
                              <td>{emp.department}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3">No employees on leave found in the selected date range</p>
                  )}
                </div>
              </div>
            </div>

            {/* Late Employees List */}
            <div className="col-12 mb-3">
              <div className="card radius-10 custom-shadow border-warning">
                <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-warning">
                    <i className="bi bi-clock-history me-2"></i>
                    Late Employees List
                  </h5>
                  {!loading && (
                    <span className="badge bg-warning fs-6">
                      Total: {lateEmployees.length}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : lateEmployees.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-striped">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Name</th>
                            <th>Date</th>
                            <th>Duty Hours</th>
                            <th>Check-in Time</th>
                            <th>Late Time</th>
                            <th>Branch</th>
                            <th>Designation</th>
                            <th>Department</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lateEmployees.map((emp, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                <strong>{emp.name}</strong>
                              </td>
                              <td>
                                <span className="badge bg-info">
                                  {moment(emp.date).format("MMM DD, YYYY")}
                                </span>
                              </td>
                              <td>
                                {emp.duty_start_time && emp.duty_start_time !== "N/A" ? (
                                  <span className="badge bg-secondary">
                                    {emp.duty_start_time} - {emp.duty_end_time || "N/A"}
                                  </span>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>
                                <span className="badge bg-primary">{emp.checkin_time}</span>
                              </td>
                              <td>
                                <span className="badge bg-warning">{emp.late_time}</span>
                              </td>
                              <td>{emp.branch}</td>
                              <td>{emp.designation}</td>
                              <td>{emp.department}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3">No late employees found in the selected date range</p>
                  )}
                </div>
              </div>
            </div>

            {/* Birthdays This Month */}
            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow">
                <div className="card-header bg-transparent">
                  <h5 className="mb-0">
                    <i className="bi bi-balloon-fill text-danger me-2"></i>
                    Birthdays This Month ({moment().format("MMMM")})
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : birthdaysThisMonth.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Branch</th>
                            <th>Designation</th>
                            <th>Department</th>
                            <th>Date</th>
                            <th>Age</th>
                          </tr>
                        </thead>
                        <tbody>
                          {birthdaysThisMonth.map((emp, index) => (
                            <tr key={index}>
                              <td>
                                <strong>{emp.first_name} {emp.last_name}</strong>
                              </td>
                              <td>{emp.branch_name || "N/A"}</td>
                              <td>{emp.designation_name || emp.designation || "N/A"}</td>
                              <td>{emp.department_name || emp.department || "N/A"}</td>
                              <td>
                                <span className="badge bg-danger">{emp.birthday_date}</span>
                              </td>
                              <td><span className="badge bg-info">{emp.age} years</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3">No birthdays this month</p>
                  )}
                </div>
              </div>
            </div>

            {/* Anniversaries This Month */}
            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow">
                <div className="card-header bg-transparent">
                  <h5 className="mb-0">
                    <i className="bi bi-calendar-heart text-primary me-2"></i>
                    Anniversaries This Month ({moment().format("MMMM")})
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : anniversariesThisMonth.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Branch</th>
                            <th>Designation</th>
                            <th>Department</th>
                            <th>Anniversary</th>
                            <th>Years</th>
                          </tr>
                        </thead>
                        <tbody>
                          {anniversariesThisMonth.map((emp, index) => (
                            <tr key={index}>
                              <td>
                                <strong>{emp.first_name} {emp.last_name}</strong>
                              </td>
                              <td>{emp.branch_name || "N/A"}</td>
                              <td>{emp.designation_name || emp.designation || "N/A"}</td>
                              <td>{emp.department_name || emp.department || "N/A"}</td>
                              <td>
                                <span className="badge bg-primary">{emp.anniversary_date}</span>
                              </td>
                              <td><span className="badge bg-success">{emp.years_of_service} years</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3">No anniversaries this month</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HRDashboard;
